/**
 * ============================================================================
 * MOCK USERS DATA
 * ============================================================================
 * Mock-данные пользователей для режима разработчика
 * Используются для быстрого переключения между профилями без авторизации
 * ============================================================================
 */

/**
 * Mock-токен для режима разработчика
 */
export const DEV_MODE_TOKEN =
	'dev_mode_mock_token_eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9'

/**
 * Mock-данные студента
 */
export const MOCK_STUDENT = {
	id: 1001,
	email: 'student@dev.local',
	fullName: 'Иван Студентов',
	firstName: 'Иван',
	lastName: 'Студентов',
	avatarUrl: 'https://i.pravatar.cc/150?img=12',
	roles: ['student'],
	permissions: [
		'courses.view',
		'lessons.view',
		'assignments.view',
		'assignments.submit',
		'grades.view',
		'profile.edit',
	],
	isEmailVerified: true,
	createdAt: '2024-01-15T10:00:00Z',
	updatedAt: '2024-12-20T15:30:00Z',
	// Дополнительные данные студента
	studentInfo: {
		studentId: 'STU-2024-001',
		group: 'ИТ-301',
		course: 3,
		faculty: 'Информационные технологии',
		enrollmentYear: 2022,
		gpa: 4.5,
	},
}

/**
 * Mock-данные преподавателя
 */
export const MOCK_TEACHER = {
	id: 2001,
	email: 'teacher@dev.local',
	fullName: 'Мария Преподавателева',
	firstName: 'Мария',
	lastName: 'Преподавателева',
	avatarUrl: 'https://i.pravatar.cc/150?img=47',
	roles: ['teacher'],
	permissions: [
		'courses.view',
		'courses.create',
		'courses.edit',
		'lessons.view',
		'lessons.create',
		'lessons.edit',
		'assignments.view',
		'assignments.create',
		'assignments.edit',
		'assignments.grade',
		'students.view',
		'enrollments.view',
		'enrollments.create',
		'reports.view',
		'profile.edit',
	],
	isEmailVerified: true,
	createdAt: '2023-09-01T08:00:00Z',
	updatedAt: '2024-12-20T16:00:00Z',
	// Дополнительные данные преподавателя
	teacherInfo: {
		teacherId: 'TCH-2023-042',
		department: 'Кафедра программирования',
		position: 'Доцент',
		degree: 'Кандидат технических наук',
		specialization: 'Веб-разработка и базы данных',
		coursesCount: 5,
		studentsCount: 120,
	},
}

/**
 * Mock-данные администратора
 */
export const MOCK_ADMIN = {
	id: 3001,
	email: 'admin@dev.local',
	fullName: 'Александр Администраторов',
	firstName: 'Александр',
	lastName: 'Администраторов',
	avatarUrl: 'https://i.pravatar.cc/150?img=33',
	roles: ['admin'],
	permissions: [
		'users.view',
		'users.create',
		'users.edit',
		'users.delete',
		'courses.view',
		'courses.create',
		'courses.edit',
		'courses.delete',
		'lessons.view',
		'lessons.create',
		'lessons.edit',
		'lessons.delete',
		'assignments.view',
		'assignments.create',
		'assignments.edit',
		'assignments.delete',
		'enrollments.view',
		'enrollments.create',
		'enrollments.edit',
		'enrollments.delete',
		'reports.view',
		'reports.export',
		'settings.view',
		'settings.edit',
		'system.manage',
		'profile.edit',
	],
	isEmailVerified: true,
	createdAt: '2023-01-10T09:00:00Z',
	updatedAt: '2024-12-20T17:00:00Z',
	// Дополнительные данные администратора
	adminInfo: {
		adminId: 'ADM-2023-001',
		accessLevel: 'full',
		department: 'IT отдел',
		position: 'Системный администратор',
		lastLogin: '2024-12-20T17:00:00Z',
	},
}

/**
 * Получить mock-данные пользователя по роли
 * @param {string} role - 'student' | 'teacher' | 'admin'
 * @returns {object|null} Mock-данные пользователя
 */
export function getMockUserByRole(role) {
	const mockUsers = {
		student: MOCK_STUDENT,
		teacher: MOCK_TEACHER,
		admin: MOCK_ADMIN,
	}

	const user = mockUsers[role]

	if (!user) {
		console.error(`❌ [MockUsers] Invalid role: ${role}`)
		return null
	}

	console.log(`✅ [MockUsers] Returning mock user for role: ${role}`, {
		email: user.email,
		fullName: user.fullName,
		roles: user.roles,
	})

	return user
}

/**
 * Получить все mock-пользователи
 * @returns {object} Объект с mock-пользователями
 */
export function getAllMockUsers() {
	return {
		student: MOCK_STUDENT,
		teacher: MOCK_TEACHER,
		admin: MOCK_ADMIN,
	}
}

/**
 * Установить mock-пользователя в localStorage и store
 * @param {string} role - 'student' | 'teacher' | 'admin'
 * @param {object} appStore - useAppStore instance
 */
export async function setMockUser(role, appStore) {
	const user = getMockUserByRole(role)

	if (!user) {
		console.error(`❌ [MockUsers] Cannot set mock user for role: ${role}`)
		return false
	}

	try {
		// Сохраняем mock-токен
		localStorage.setItem('access_token', DEV_MODE_TOKEN)
		localStorage.setItem('refresh_token', DEV_MODE_TOKEN)

		// Сохраняем данные пользователя
		localStorage.setItem('app-user', JSON.stringify(user))

		// Обновляем store
		if (appStore) {
			appStore.setUser(user)
			appStore.setPermissions(user.permissions)
		}

		console.log(`✅ [MockUsers] Mock user set successfully:`, {
			role,
			email: user.email,
			fullName: user.fullName,
		})

		return true
	} catch (error) {
		console.error('❌ [MockUsers] Error setting mock user:', error)
		return false
	}
}

/**
 * Очистить mock-данные пользователя
 */
export function clearMockUser() {
	try {
		localStorage.removeItem('access_token')
		localStorage.removeItem('refresh_token')
		localStorage.removeItem('app-user')

		console.log('🗑️ [MockUsers] Mock user data cleared')
	} catch (e) {
		console.error('❌ [MockUsers] Error clearing mock user:', e)
	}
}

/**
 * Проверить, используется ли mock-пользователь
 * @returns {boolean}
 */
export function isMockUser() {
	try {
		const token = localStorage.getItem('access_token')
		return token === DEV_MODE_TOKEN
	} catch (e) {
		return false
	}
}

/**
 * Получить роль текущего mock-пользователя
 * @returns {string|null} 'student' | 'teacher' | 'admin' | null
 */
export function getCurrentMockUserRole() {
	try {
		const userJson = localStorage.getItem('app-user')
		if (!userJson) return null

		const user = JSON.parse(userJson)
		if (!user.roles || user.roles.length === 0) return null

		return user.roles[0]
	} catch (error) {
		console.error('❌ [MockUsers] Error getting current mock user role:', error)
		return null
	}
}
